import React from 'react';
import Navigation from '../components/Navigation';
import { supabase } from '../lib/supabase';

interface CookiesPolicyProps {
  navigate: (to: string) => void;
}

export default function CookiesPolicy({ navigate }: CookiesPolicyProps) {
  return (
    <div className="min-h-screen bg-white">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-sm rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Cookies Policy</h1>
          <p className="text-sm text-gray-500 mb-8">Effective Date: 10/04/25</p>
          
          <div className="prose prose-indigo max-w-none">
            <p className="text-gray-700">
              This Cookies Policy explains how PM Pro AI ("we", "us", or "our") uses cookies and similar technologies when you use our web-based application.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">1. What Are Cookies?</h2>
            <p className="text-gray-700">
              Cookies are small text files stored on your device (computer, smartphone, or tablet) when you visit a website. They help improve your browsing experience and allow us to understand how the application is being used.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">2. Why We Use Cookies</h2>
            <p className="text-gray-700">
              We use cookies to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Maintain session states and keep you logged in</li>
              <li>Remember user preferences and project-related settings</li>
              <li>Enable core functionality of the platform</li>
              <li>Secure access to user-specific content (like custom agents or project data)</li>
            </ul>
            <p className="text-gray-700">
              We do NOT use cookies for:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Advertising or marketing</li>
              <li>Behavioral tracking across websites</li>
              <li>Analytics services from third parties</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">3. Types of Cookies We Use</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 mb-6">
                <thead>
                  <tr>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cookie Type
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Purpose
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      Essential Cookies
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      Required for login, security, and basic app functionality
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      Preference Cookies
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      Store user preferences like theme, language, or layout
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      Session Cookies
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      Temporarily store session-related information during login
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-gray-700">
              These cookies are only used while you are actively using the application.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">4. Third-Party Cookies</h2>
            <p className="text-gray-700">
              We do not use third-party advertising or tracking cookies. If any integration (like OpenAI API or authentication provider) sets their own cookies, please refer to their respective policies.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">5. Managing Cookies</h2>
            <p className="text-gray-700">
              You can control and delete cookies through your browser settings. However, disabling essential cookies may affect the core functionality of PM Pro AI and could prevent you from accessing certain parts of the platform.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">6. Updates to This Policy</h2>
            <p className="text-gray-700">
              We may update this policy from time to time. Any changes will be posted on this page with an updated effective date.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">7. Contact Us</h2>
            <p className="text-gray-700">
              If you have any questions about this Cookies Policy, please reach out at:
            </p>
            <p className="text-gray-700">
              📧 <a href="mailto:hello@pmproai.com" className="text-indigo-600 hover:text-indigo-500">hello@pmproai.com</a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}